import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State } from '@stencil/core';

import { ClassNames } from '@/src/utils/interfaces';

export type LhTexttoggleItemDataV1 = {
  selected: boolean;
  disabled: boolean;
  element: HTMLPatternlibTexttoggleItemV1Element;
};

@Component({
  tag: 'patternlib-texttoggle-item-v1',
  styleUrl: 'patternlib-texttoggle-item-v1.scss',
  shadow: true,
})
export class PatternlibTexttoggleItemV1 {
  private buttonRef: HTMLButtonElement | null = null;

  @Element() hostElement: HTMLPatternlibTexttoggleItemV1Element;

  /** @internal */
  @Prop() selected = false;

  /** @internal */
  @Prop() disabled = false;

  /**
   * Texttoggle item label.
   */
  @Prop() label = '';

  /** @internal
   * pts: pointer target size
   */
  @Prop() ptsExpansions = true;

  /** @internal */
  @Prop() size: 'big' | 'medium' | 'small' | 'extra-small' | 'L' | 'M' | 'S' | 'XS' = 'L';

  /** @internal */
  @Prop() debug = false;

  /**
   * The value of the texttoggle item.
   */
  @Prop() value = '';

  /**
   * Eighter the label or the slot content will be used as aria-label.
   */
  @State() ariaLabelState = '';

  /** @internal */
  @Event({ eventName: 'lhItemSelect' }) selectEvent: EventEmitter<LhTexttoggleItemDataV1>;

  @Method()
  async setFocus(): Promise<void> {
    this.buttonRef?.focus();
  }

  componentWillLoad(): void {
    this.ariaLabelState = this.label;

    if (this.value == null) {
      console.warn(
        'Required property `value` on `patternlib-texttoggle-item-v1` is missing. The component may not work as intended.',
      );
    }
  }

  componentDidLoad(): void {
    // aria-label from slot content
    if (this.ariaLabelState === '') {
      this.ariaLabelState = this.retrieveSlotTextContent();
    }
  }

  componentWillRender(): void {
    setTimeout(() => {
      this.updateExpansionWidths();
    }, 0);
  }

  render() {
    const hostStyle = {
      userSelect: 'none',
      pointerEvents: this.disabled ? 'none' : null,
    };

    const buttonElementClasses: ClassNames = {
      'button-element': true,
      'disabled': this.disabled,
      'selected': this.selected,
      [this.size]: true,
    };

    return (
      <Host style={hostStyle}>
        {this.ptsExpansions && (
          <div
            class={`pts-expansion-upper ${this.size} ${this.debug ? 'colorize-pts' : ''}`}
            onClick={this.handleClick}
          />
        )}
        <button
          role="radio"
          aria-disabled={this.disabled ? 'true' : 'false'}
          aria-checked={this.selected ? 'true' : 'false'}
          aria-label={this.ariaLabelState}
          class={buttonElementClasses}
          onClick={this.handleClick}
          ref={el => (this.buttonRef = el)}
        >
          <slot>{this.label}</slot>
        </button>
        {this.ptsExpansions && (
          <div
            class={`pts-expansion-lower ${this.size} ${this.debug ? 'colorize-pts' : ''}`}
            onClick={this.handleClick}
          />
        )}
      </Host>
    );
  }

  private handleClick = (evt: MouseEvent): void => {
    evt.stopPropagation();
    this.selectEvent.emit({
      element: this.hostElement,
      disabled: this.disabled,
      selected: this.selected,
    });
  };

  private retrieveSlotTextContent(): string {
    let slotText = '';
    const slot = this.hostElement.shadowRoot.querySelector('slot');
    const assignedNodes = slot?.assignedNodes();
    assignedNodes?.forEach(node => {
      if (slotText === '' && node.nodeType === Node.TEXT_NODE) {
        slotText = node.textContent?.trim();
      }
    });
    return slotText;
  }

  private updateExpansionWidths() {
    if (this.buttonRef) {
      const buttonWidth = this.buttonRef.clientWidth;
      const upper = this.hostElement.shadowRoot.querySelector('.pts-expansion-upper') as HTMLElement;
      const lower = this.hostElement.shadowRoot.querySelector('.pts-expansion-lower') as HTMLElement;

      if (upper) upper.style.width = `${buttonWidth}px`;
      if (lower) lower.style.width = `${buttonWidth}px`;
    }
  }
}
